import { DEFAULT_ORDER_STATUS } from "@bcc/components/orders/constants";
import { toast } from "@bcc/hooks/use-toast";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import type { InitializePurchaseOrderState } from "@client-shared/providers/purchase-orders";
import type { CarrierCustomerAccountBinding, PurchaseOrderStatus } from "@shared/types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { NavigateFunction } from "react-router-dom";

interface UseOrdersInitializationParams {
  readonly isVendorInitialized: boolean;
  readonly carrierAccountId: string | undefined;
  readonly purchaseOrderId?: string;
  readonly customerAccountNumbers: string[] | null;
  readonly customerAccountCarrierDetails: CarrierCustomerAccountBinding[] | undefined;
  readonly searchParams: URLSearchParams;
  readonly navigate: NavigateFunction;
  readonly initialize: (state: InitializePurchaseOrderState) => void;
}

export function useOrdersInitialization({
  isVendorInitialized,
  carrierAccountId,
  purchaseOrderId,
  customerAccountNumbers,
  customerAccountCarrierDetails,
  searchParams,
  navigate,
  initialize,
}: UseOrdersInitializationParams): void {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isVendorInitialized) return;

    // Redirect to first carrier if none in URL
    if (!carrierAccountId && (customerAccountCarrierDetails ?? []).length > 0) {
      const defaultCarrier = customerAccountCarrierDetails?.[0];
      const orderPathSegment = purchaseOrderId ? `/${encodeURIComponent(purchaseOrderId)}` : "";
      const pathname = defaultCarrier ? `${ROUTE_PATHS.MARKETPLACE}/${defaultCarrier.id}/orders${orderPathSegment}` : ROUTE_PATHS.ORDERS;
      navigate({ pathname, search: searchParams.toString() });
      return;
    }

    // Decode failure → home
    if (carrierAccountId && customerAccountNumbers === null) {
      toast({
        title: t("components.PurchaseOrder.invalidCustomerAccount"),
        variant: "destructive",
      });
      navigate(ROUTE_PATHS.HOME);
      return;
    }

    // Validate decoded accounts against known carrier details
    if (carrierAccountId && (customerAccountCarrierDetails ?? []).length > 0) {
      const hasValidAccount = customerAccountCarrierDetails?.some((details) => {
        const detailsAccountNumbers = details.customerAccountNumber.split(",").map((num) => num.trim());
        return detailsAccountNumbers.some((accountNum) => (customerAccountNumbers ?? []).includes(accountNum));
      });
      if (!hasValidAccount) {
        toast({
          title: t("components.PurchaseOrder.invalidCustomerAccount"),
          variant: "destructive",
        });
        navigate(ROUTE_PATHS.HOME);
        return;
      }
    }

    // Parse orderStatus from URL, defaulting to WaitingToBeFulfilled
    const rawStatus = searchParams.get("orderStatus");
    const orderStatus: PurchaseOrderStatus[] = rawStatus ? (rawStatus.split(",") as PurchaseOrderStatus[]) : [DEFAULT_ORDER_STATUS];

    initialize({
      params: {
        customerAccountNumbers: customerAccountNumbers ?? [],
        sortByCreatedAt: "desc",
        orderStatus,
      },
    });
  }, [
    t,
    initialize,
    carrierAccountId,
    purchaseOrderId,
    customerAccountNumbers,
    searchParams,
    isVendorInitialized,
    customerAccountCarrierDetails,
    navigate,
  ]);
}
